import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button, Spinner } from 'react-bootstrap';
import { askAI, addUserMessage, clearAIConversation } from '../../redux/knowledgeBaseSlice';

const AskAITab = () => {
  const dispatch = useDispatch();
  const { aiConversation, aiLoading, aiConversationId, aiError } = useSelector(state => state.knowledgeBase);
  const [input, setInput] = useState('');
  const bottomRef = useRef(null);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiConversation, aiLoading]);

  useEffect(() => {
    if (!aiLoading) {
      isSubmittingRef.current = false;
    }
  }, [aiLoading]);

  const handleSend = () => {
    // Guards against a fast double-click/double-Enter firing two requests before the first one completes.
    if (!input.trim() || aiLoading || isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const question = input.trim();
    setInput('');

    dispatch(addUserMessage(question));
    dispatch(askAI({
      question,
      conversationHistory: [...aiConversation, { role: 'user', content: question }],
      conversationId: aiConversationId,
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="bg-white border rounded p-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 className="mb-1">Ask our AI Assistant</h5>
          <small className="text-muted">Powered by Claude — answers based on our knowledge base articles</small>
        </div>
        {aiConversation.length > 0 && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => dispatch(clearAIConversation())}
          >
            Clear chat
          </Button>
        )}
      </div>

      {/* Messages */}
      <div style={{ height: 'calc(100vh - 340px)', overflowY: 'auto', paddingRight: '4px' }}>
        {aiConversation.length === 0 && (
          <div className="text-center py-5 text-muted">
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>💬</div>
            <p>Ask me anything about tax sales, bidding, redemption periods, Zeus auctions, or payment terms.</p>
          </div>
        )}

        {aiConversation.map((msg, i) => (
          <div key={i} className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
            {msg.role === 'assistant' && (
              <div
                className="flex-shrink-0 me-2 d-flex align-items-center justify-content-center rounded-circle"
                style={{ width: '28px', height: '28px', background: '#e85d04', color: '#fff', fontSize: '12px', fontWeight: 800, marginTop: '2px' }}
              >
                S
              </div>
            )}
            <div
              style={{
                maxWidth: '78%',
                background: msg.role === 'user' ? '#e85d04' : '#f8f8f6',
                borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '4px 18px 18px 18px',
                padding: '10px 14px',
                color: msg.role === 'user' ? '#fff' : '#333',
                fontSize: '13.5px',
                lineHeight: 1.65,
              }}
            >
              {msg.content.split('\n').map((line, j) => {
                if (!line.trim()) return <br key={j} />;
                if (line.startsWith('• ') || line.startsWith('- ')) {
                  return <div key={j} style={{ paddingLeft: '12px', textIndent: '-12px' }}>{line}</div>;
                }
                return <div key={j}>{line}</div>;
              })}
            </div>
          </div>
        ))}

        {aiLoading && (
          <div className="d-flex align-items-center gap-2">
            <div
              className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle"
              style={{ width: '28px', height: '28px', background: '#e85d04', color: '#fff', fontSize: '12px', fontWeight: 800 }}
            >
              S
            </div>
            <Spinner animation="grow" size="sm" style={{ color: '#e85d04' }} />
          </div>
        )}

        {aiError && (
          <div className="text-danger small mb-2">
            {aiError}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="d-flex gap-2 pt-3 border-top">
        <Form.Control
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question about tax sales, Zeus, redemption…"
          aria-label="Ask the AI assistant a question"
          style={{ borderColor: '#e0e0e0' }}
        />
        <Button
          onClick={handleSend}
          disabled={aiLoading || !input.trim()}
          style={{ background: '#e85d04', border: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          Send
        </Button>
      </div>
    </div>
  );
};

export default AskAITab;
